import { Element } from "@xmpp/xml"
import { RouteStop } from "@/reducers/routes2gSlice"
import Route2gParser from "@/lib/route2gParser"
import { RouteInfo, Toll } from "@/components/maps/RouteView"
import { AvoidType, CarRouteConfig, LoadType } from "@/api/adHocCommands"
import { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"

export type RouteStopWithHandle = RouteStop & {
  route_handle: string
  polyline: string
  distance: number
  duration: number
}

export type AlternativeRoute = {
  type: string // "Cheapest", "Shortest", "Recommended"
  stops: RouteStopWithHandle[]
  tolls?: Toll[]
  routeInfo: RouteInfo
  fuel?: number
  carRouteConfigAvoid?: AvoidType[]
}

export type PerLegAlternatives = {
  fromStopIndex: number
  toStopIndex: number
  alternatives: AlternativeRoute[]
}

export type OrderAnalyzeRoute = {
  jid: string
  lastUpdatedBy?: string
  stops: RouteStop[]
  route: RouteInfo
  alternativeRoutes?: AlternativeRoute[]
  routeMetaData?: any // Additional data from route-meta stanza
  avoidOptions?: AvoidType[]
  loadType?: LoadType[]
  vehicleType?: VehicleType
  selectedDriver?: SelectedDriverLocation
  driverCarConfig?: CarRouteConfig
  perLegAlternatives?: PerLegAlternatives[]
}

class PlanRouteParser {
  static readonly ROUTE_NAMESPACE = "urn:slickshift:route:0"

  static parse(stanza: Element): OrderAnalyzeRoute | undefined {
    const command = stanza.getChild(
      "command",
      "http://jabber.org/protocol/commands",
    )

    const routeElements = command?.getChildren(
      "route",
      PlanRouteParser.ROUTE_NAMESPACE,
    )

    if (!routeElements) return

    const routes = routeElements?.map((routeElement, index) => {
      const stopsWithHandles = routeElement
        .getChildren("stop")
        .flatMap((item) => {
          const stop = Route2gParser.parseRouteStop(item)
          if (!stop) return []
          stop.is_temporary = true
          const route_handle = item.getAttr("route_handle") || ""

          const polylineElement = item.getChild("polyline")
          const polyline = polylineElement
            ? polylineElement.getText()
            : undefined
          if (!polyline) return []

          const distance = parseInt(
            polylineElement?.getAttr("distance") ?? "0",
            10,
          )
          const duration = parseInt(
            polylineElement?.getAttr("duration") ?? "0",
            10,
          )

          return [
            {
              ...stop,
              route_handle,
              polyline,
              distance,
              duration,
            } as RouteStopWithHandle,
          ]
        })

      const tolls = this.parseTolls(routeElement.getChild("tolls"))
      const type = routeElement.getAttr("type")
      const label = routeElement.getAttr("label") || ""
      const fuel = parseFloat(routeElement.getAttr("fuel_consumption")) || 0
      const carRouteConfigAvoidStr = routeElement.getAttr(
        "car_route_config_avoid",
      )
      const carRouteConfigAvoid: AvoidType[] = carRouteConfigAvoidStr
        ? (carRouteConfigAvoidStr.split(",").filter(Boolean) as AvoidType[])
        : []

      // Use type if available, otherwise use label, otherwise use index
      const routeType = type || label || `route-${index}`

      return {
        stopsWithHandles,
        tolls,
        type: routeType,
        label,
        fuel,
        carRouteConfigAvoid,
      }
    })

    const alternativeRoutes = routes.map((route) => {
      return {
        type: route.type,
        routeInfo: {
          routes: [
            {
              legs: route.stopsWithHandles.map(
                ({ polyline, distance, duration }) => ({
                  polyline: polyline,
                  distance: distance,
                  duration: duration,
                }),
              ),
              distance: route.stopsWithHandles.reduce(
                (acc, curr) => acc + curr.distance,
                0,
              ),
              tolls: route.tolls,
              fuel: route.fuel,
              id: route.type, // Use type as id for backward compatibility
              label: route.label,
              isAlternative: true,
            },
          ],
        },
        stops: route.stopsWithHandles.map((stop, i, arr) => ({
          ...stop,
          remaining_distance: arr
            .slice(0, i + 1)
            .reduce((acc, s) => acc + s.distance, 0),
        })),
        tolls: route.tolls,
        fuel: route.fuel,
        carRouteConfigAvoid: route.carRouteConfigAvoid,
      }
    })

    return {
      jid: stanza.attrs.from,
      stops: alternativeRoutes?.[0].stops,
      route: alternativeRoutes?.[0].routeInfo,
      alternativeRoutes,
    }
  }

  static parseToll(tollElement: Element): Toll | undefined {
    if (!tollElement) return
    const fare_id = tollElement.getAttr("fare_id")
    const country_code = tollElement.getAttr("country_code")
    const price = parseFloat(tollElement.getAttr("price")) || 0
    const currency = tollElement.getAttr("currency")
    if (!country_code || !price || !currency) return
    return {
      fare_id,
      country_code,
      price,
      currency,
    }
  }

  static parseTolls(tollsElement: Element | undefined): Toll[] {
    if (!tollsElement) return []
    return tollsElement
      .getChildren("toll")
      ?.map((toll) => PlanRouteParser.parseToll(toll))
      .filter((toll) => toll !== undefined) as Toll[]
  }
}

export default PlanRouteParser
